// Selectors for the incentive/bonus flows: the "free item" offers computed
// from cart/order totals (cartUtils.eligibleIncentivesForOrder /
// eligibleIncentivesForCart / availableOptionalIncentives) and the
// personalization / pre-cart-offer prompts that ride alongside them in the
// PreCartOffers / PreDashboardOffers gate.
//
// These selectors are the single place that combine the cartUtils helpers
// with the surrounding state (current order, international-shipping
// restriction, cart-level catalog union, voucher totals, ignore lists) so
// PreCartOffers, PreDashboardOffers, AvailableBonuses, IncentivesConfig and
// IncentiveTracker can each just `useSelector` a finished value instead of
// re-deriving it inline. The cartUtils functions themselves stay the source
// of truth for the actual eligibility math — nothing here reimplements them.
//
// Two catalog derivations live here as well (ENG-2965):
//
//   1. drop incentives the parent's international-shipping restriction makes
//      unavailable (availableIncentiveProducts + isShippingRestrictedForParent);
//   2. union in the cart-level incentives stashed on every *other* order in
//      the cart, so a cart-level benefit stays qualified no matter which
//      price list the parent happens to be viewing.
//
// These are plain functions of state, so postConfigRoutingMiddleware calls
// them with getState() the same way components call them with useSelector.
import { createSelector } from 'reselect';
import { selectConfigurationContext } from './configurationContext';
import cartUtils from '../utils/cart';
import {
  isShippingRestrictedForParent,
  availableIncentiveProducts,
} from '../components/v3/helpers/internationalRestriction';

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

export const selectCartVoucherTotalCents = (state: any): number =>
  state.cartVoucherItems?.voucherSubtotalCents || 0;

export const selectCartHasVouchers = (state: any): boolean =>
  (state.cartVoucherItems?.items?.length || 0) > 0;

// The ACTIVE shoot's company opt-in flag. Nothing in the cart may read this
// directly any more (ENG-2043 R1): it describes the last-opened gallery, not
// the order being judged. It survives only as the fallback inside
// selectOrderOptInMap for an order persisted before the per-order flag
// existed, and only while that order is the parent's current one.
export const selectOptionalIncentivesEnabled = (state: any): boolean =>
  state.shoot?.optionalIncentivesEnabled;

export const selectActiveEventId = (state: any): any => {
  const shootInfo = state.shoot?.shoot;
  return shootInfo?.event_id || shootInfo?.id || null;
};

// The gallery the active shoot data was loaded for. One shoot can serve
// several galleries with different price lists (buyer vs non-buyer reorder),
// so this is what decides whether the loaded shoot really describes a given
// order.
//
// TWO SPELLINGS, both real. `picsGalleryId` is the one the ordering flows
// actually populate: galleryOrderContext writes it from
// `gallery.pics_gallery_id` when a dashboard gallery is opened, and the
// key-entry / student-selection flows write it from the same field on the
// response. `gallery_id` is the snake_case spelling addToCart stamps onto the
// order's own `additionalData`, and the one the GET_SHOOT_DATA stash refresh
// compares against `pics_gallery_id` (reducers/v3Order/index.js). Read both,
// preferring the flow-populated one. (`state.shoot.galleryId` was read here
// before and is never set by reducer_shoot.js — always undefined.)
const selectActiveGalleryId = (state: any): any =>
  state.additionalData?.picsGalleryId ??
  state.additionalData?.gallery_id ??
  null;

// The same value from an ORDER's captured additionalData, which is a snapshot
// of the parent's additionalData at add-to-cart time and so can carry either
// spelling (see selectActiveGalleryId).
const orderGalleryId = (order: any): any =>
  order?.additionalData?.gallery_id ??
  order?.additionalData?.picsGalleryId ??
  null;

// Set while a bonus is being re-added from the cart's Available Bonuses list;
// names the order the re-add belongs to and the product to configure.
export const selectOptionalIncentiveToConfigure = (state: any): any =>
  state.v3Order?.optionalIncentiveToConfigure;

// True when the parent's shoot blocks international shipping and the parent
// is outside the US.
export const selectShippingRestricted = (state: any): boolean =>
  isShippingRestrictedForParent(state.shoot?.shoot, state.parent);

// International-shipping-aware incentive catalog: incentives requiring
// shipping, or offering free shipping, are dropped when the parent's shoot
// blocks international shipping and the parent is outside the US. See
// availableIncentiveProducts for the two flags this drops.
export const selectRestrictionAwareIncentiveProducts = createSelector(
  (state: any) => state.shoot?.incentiveProducts,
  (state: any) => state.shoot?.shoot,
  (state: any) => state.parent,
  (incentiveProducts, shoot, parent) =>
    availableIncentiveProducts(
      incentiveProducts,
      isShippingRestrictedForParent(shoot, parent),
    ),
);

// Parents outside the US must not be offered free shipping stashed from a
// shoot that blocks international shipping — the union filters on this.
export const selectParentCountry = (state: any): string =>
  state.parent?.address?.country || 'US';

// The catalog cart-level eligibility must be checked against: the active
// shoot's cart-level incentives plus the ones stashed on every other order
// in the cart, deduped by SKU at the lowest threshold (ENG-2965). Every
// cart-level eligibility selector below reads this, never the active
// catalog alone.
export const selectCartLevelIncentiveProducts = createSelector(
  (state: any) => state.v3Order?.orders,
  selectRestrictionAwareIncentiveProducts,
  selectParentCountry,
  (orders, incentiveProducts, parentCountry): any[] =>
    cartUtils.unionCartLevelIncentiveProducts(
      orders,
      incentiveProducts,
      parentCountry,
    ),
);

// Everything the CART needs to reason about cart-level benefits: the offer
// union above surfaces stashed rows only when they are free-shipping (they
// cannot be configured outside their own shoot), but the cart lists declined
// bonuses and cleans up restricted shipping for every list in the cart, so it
// reads the full listing union instead (ENG-2043 R4).
export const selectCartLevelIncentiveListingUnion = createSelector(
  (state: any) => state.v3Order?.orders,
  selectRestrictionAwareIncentiveProducts,
  selectParentCountry,
  (orders, incentiveProducts, parentCountry): any[] =>
    cartUtils.unionCartLevelIncentiveProducts(
      orders,
      incentiveProducts,
      parentCountry,
      { includeConfigurable: true },
    ),
);

// Every free-shipping incentive row the cart could be holding, from the
// active catalog AND every order's own stash (both scopes). Deliberately
// unfiltered by the international rule and not deduped by SKU: this is what
// IDENTIFIES a free-shipping row already in the cart so the restricted-parent
// cleanup can remove it — the rows it must find are exactly the ones the
// restriction filter drops from the offer/listing unions (ENG-2043 R6).
export const selectFreeShippingIncentiveProducts = createSelector(
  (state: any) => state.shoot?.incentiveProducts,
  (state: any) => state.v3Order?.orders,
  (activeIncentiveProducts, orders): any[] => {
    const rows = [...(activeIncentiveProducts || [])];

    Object.values(orders || {}).forEach((order: any) => {
      rows.push(
        ...(order?.incentiveProducts || []),
        ...(order?.orderLevelIncentiveProducts || []),
      );
    });

    return rows.filter((product) => product?.is_free_shipping === true);
  },
);

// IncentiveTracker's progress bar: the active catalog's order-level
// incentives, plus the cart-level union. Without the union the "Earned: Free
// Shipping" segment would flip depending on which school the parent is
// viewing.
export const selectIncentiveProductsToTrack = createSelector(
  selectRestrictionAwareIncentiveProducts,
  selectCartLevelIncentiveProducts,
  (incentiveProducts, cartLevelIncentiveProducts): any[] => [
    ...(incentiveProducts || []).filter(
      (product: any) => product.incentive_applies_to !== 'cart_level',
    ),
    ...cartLevelIncentiveProducts,
  ],
);

// ---------------------------------------------------------------------------
// Current order (the parent's active shootEvent-student order) — same key
// construction as useCurrentOrderCartItems and postConfigRoutingMiddleware:
// `${shootEventId}-${studentId}`, null before a student is selected.
// ---------------------------------------------------------------------------

export const selectCurrentOrderKey = (state: any): string | null => {
  const shootInfo = state.shoot?.shoot;
  const shootEventId = shootInfo?.event_id || shootInfo?.id;
  const studentId = state.additionalData?.selectedStudent?.id;
  return shootEventId && studentId ? `${shootEventId}-${studentId}` : null;
};

export const selectCurrentOrder = createSelector(
  (state: any) => state.v3Order?.orders,
  selectCurrentOrderKey,
  (orders, orderKey): any => (orderKey ? orders?.[orderKey] : undefined),
);

export const selectCurrentOrderCartItems = createSelector(
  selectCurrentOrder,
  (order): any[] => order?.cartItems || [],
);

export const selectShouldPromptForPersonalization = createSelector(
  selectCurrentOrder,
  (order): boolean => Boolean(order?.personalization?.shouldPrompt),
);

export const selectCartContainsPersonalizableProducts = createSelector(
  selectCurrentOrderCartItems,
  (cartItems): boolean =>
    cartUtils.cartContainsPersonalizableProducts(cartItems),
);

// ---------------------------------------------------------------------------
// Per-order optionality (ENG-2043 R1). A combined cart holds orders from
// several shoots and possibly several studios, so "can this bonus be
// declined?" is answered by the order that earned it, never by the shoot the
// parent happened to open last.
// ---------------------------------------------------------------------------

const galleryIdsMatch = (orderGalleryId: any, activeGalleryId: any): boolean =>
  orderGalleryId == null ||
  activeGalleryId == null ||
  orderGalleryId === activeGalleryId;

// The current order key, but only while the active shoot data really
// describes that order (same gallery when both sides know theirs). This is
// the ONLY order the active shoot's flag and catalog may stand in for.
export const selectCurrentOrderKeyForFallback = createSelector(
  (state: any) => state.v3Order?.orders,
  selectCurrentOrderKey,
  selectActiveGalleryId,
  (orders, currentOrderKey, activeGalleryId): string | null => {
    if (!currentOrderKey) return null;
    const order = orders?.[currentOrderKey];
    if (!order) return currentOrderKey;
    return galleryIdsMatch(orderGalleryId(order), activeGalleryId)
      ? currentOrderKey
      : null;
  },
);

// orderKey -> is this order's studio opt-in? Legacy orders (no flag) are
// opt-in only when they are the current order and the active shoot says so;
// otherwise they count as NOT opt-in, so Remove / "No thanks" is never
// offered on a possibly mandatory bonus.
export const selectOrderOptInMap = createSelector(
  (state: any) => state.v3Order?.orders,
  selectCurrentOrderKeyForFallback,
  selectOptionalIncentivesEnabled,
  (
    orders,
    currentOrderKey,
    activeOptionalIncentivesEnabled,
  ): Record<string, boolean> => {
    const optInByOrderKey: Record<string, boolean> = {};
    Object.entries(orders || {}).forEach(([orderKey, order]) => {
      optInByOrderKey[orderKey] = cartUtils.orderIsOptIn(order, {
        isCurrentOrder: orderKey === currentOrderKey,
        activeOptionalIncentivesEnabled:
          activeOptionalIncentivesEnabled === true,
      });
    });
    return optInByOrderKey;
  },
);

// The orders whose shoot IS the one currently loaded into state.shoot — the
// only orders whose bonuses can be configured without loading anything first
// (ENG-2043 re-add Tier 2). Same event, and the same gallery when both sides
// know theirs: one shoot can serve several galleries on different price
// lists, and the configurator must use the one the order was placed from.
interface OrderShootContext {
  event?: { id?: unknown } | null;
  additionalData?: { gallery_id?: unknown } | null;
}

export const selectOrderKeysInActiveShoot = createSelector(
  (state: { v3Order?: { orders?: Record<string, OrderShootContext> } }) =>
    state.v3Order?.orders,
  selectActiveEventId,
  selectActiveGalleryId,
  (
    orders: Record<string, OrderShootContext> | undefined,
    activeEventId,
    activeGalleryId,
  ): string[] =>
    activeEventId == null
      ? []
      : Object.entries(orders || {})
          .filter(
            ([, order]) =>
              order?.event?.id === activeEventId &&
              galleryIdsMatch(orderGalleryId(order), activeGalleryId),
          )
          .map(([orderKey]) => orderKey),
);

export const selectCartHasOptInOrders = createSelector(
  selectOrderOptInMap,
  (optInByOrderKey): boolean => Object.values(optInByOrderKey).some(Boolean),
);

export const selectCurrentOrderIsOptIn = createSelector(
  selectOrderOptInMap,
  selectCurrentOrderKey,
  (optInByOrderKey, orderKey): boolean =>
    Boolean(orderKey && optInByOrderKey[orderKey]),
);

// Ignore lists apply only for an opt-in order, and only once a current order
// exists — mirrors the identical guard duplicated across PreCartOffers,
// PreDashboardOffers and postConfigRoutingMiddleware.
export const selectIgnoredOrderIncentiveIds = createSelector(
  selectCurrentOrderIsOptIn,
  (state: any) => state.v3Order?.optionalIncentiveProductIdsToIgnoreByOrder,
  selectCurrentOrderKey,
  (currentOrderIsOptIn, ignoresByOrder, orderKey): any[] =>
    currentOrderIsOptIn && orderKey ? ignoresByOrder?.[orderKey] || [] : [],
);

// Cart-level declines are kept by SKU, and honoured only when every order in
// the cart whose list offers that SKU is opt-in (shared-SKU rule, R3): a
// mandatory studio's free shipping must keep being offered even when another
// studio's identical benefit was declined, or its submit gate 422s.
export const selectIgnoredCartLevelIncentiveSkus = createSelector(
  (state: any) => state.v3Order?.orders,
  (state: any) => state.v3Order?.optionalCartLevelIncentiveSkusToIgnore,
  selectOrderOptInMap,
  selectCurrentOrderKey,
  (orders, ignores, optInByOrderKey, currentOrderKey): any[] =>
    (ignores || []).filter((sku: any) =>
      cartUtils.cartLevelSkuIsDeclinable(
        orders,
        sku,
        optInByOrderKey,
        currentOrderKey ? optInByOrderKey[currentOrderKey] : false,
      ),
    ),
);

// preCartOfferProductsToPrompt is identical for both offer gates — it never
// consults vouchers.
export const selectPreCartOfferProductsToPrompt = createSelector(
  selectCurrentOrder,
  (state: any) => state.shoot?.preCartOfferProducts,
  (state: any) => state.shoot?.triggersPreCartOfferProducts,
  (currentOrder, preCartOfferProducts, triggersPreCartOfferProducts): any[] =>
    cartUtils.preCartOfferProductsToPrompt(
      currentOrder,
      preCartOfferProducts,
      triggersPreCartOfferProducts,
    ),
);

// ---------------------------------------------------------------------------
// PreCartOffers vs. PreDashboardOffers built the same eligibility checks, but
// PreDashboardOffers never read cartVoucherItems and always passed
// voucherTotalCents=0 / hasVouchers=false — that difference is preserved
// here (not "fixed") since this is a pure relocation of existing logic, not a
// behavior change. (Voucher value only ever counts toward cart-level
// thresholds — see cartUtils.eligibleIncentivesForOrder, ENG-2965.) Each pair below shares one combiner via a small builder
// function so the duplication reviewer flagged lives in one place instead of
// two copies of the derivation.
// ---------------------------------------------------------------------------

const zeroCentsSelector = (): number => 0;
const noVouchersSelector = (): boolean => false;

const buildEligibleIncentivesForCartSelector = (
  voucherTotalCentsSelector: (state: any) => number,
) =>
  createSelector(
    (state: any) => state.v3Order?.orders,
    // Cart-level incentives can live on any price list in the cart, not just
    // the active shoot's (ENG-2965).
    selectCartLevelIncentiveProducts,
    voucherTotalCentsSelector,
    selectIgnoredCartLevelIncentiveSkus,
    (orders, incentiveProducts, voucherTotalCents, ignoredSkus): any[] =>
      cartUtils.eligibleIncentivesForCart(
        orders,
        incentiveProducts,
        voucherTotalCents,
        ignoredSkus,
      ),
  );

// Used by PreCartOffers (checkout includes the current voucher subtotal).
export const selectEligibleIncentivesForCart =
  buildEligibleIncentivesForCartSelector(selectCartVoucherTotalCents);

// Used by PreDashboardOffers, which never factored vouchers into this check.
export const selectEligibleIncentivesForCartIgnoringVouchers =
  buildEligibleIncentivesForCartSelector(zeroCentsSelector);

const buildEligibleIncentivesForOrderSelector = (
  voucherTotalCentsSelector: (state: any) => number,
  hasVouchersSelector: (state: any) => boolean,
) =>
  createSelector(
    selectCurrentOrderCartItems,
    selectRestrictionAwareIncentiveProducts,
    voucherTotalCentsSelector,
    hasVouchersSelector,
    selectIgnoredOrderIncentiveIds,
    (
      cartItems,
      incentiveProducts,
      voucherTotalCents,
      hasVouchers,
      ignoredIds,
    ): any[] =>
      cartUtils.eligibleIncentivesForOrder(
        cartItems,
        incentiveProducts,
        voucherTotalCents,
        hasVouchers,
        ignoredIds,
      ),
  );

export const selectEligibleIncentivesForOrder =
  buildEligibleIncentivesForOrderSelector(
    selectCartVoucherTotalCents,
    selectCartHasVouchers,
  );

export const selectEligibleIncentivesForOrderIgnoringVouchers =
  buildEligibleIncentivesForOrderSelector(
    zeroCentsSelector,
    noVouchersSelector,
  );

const buildCartContainsNonIncentiveProductsSelector = (
  hasVouchersSelector: (state: any) => boolean,
) =>
  createSelector(
    selectCurrentOrderCartItems,
    hasVouchersSelector,
    (cartItems, hasVouchers): boolean =>
      // Vouchers count as non-incentive products for qualification purposes.
      cartUtils.cartContainsNonIncentiveProducts(cartItems) || hasVouchers,
  );

// PreCartOffers: `|| hasVouchers` with the real voucher state.
export const selectCartContainsNonIncentiveProducts =
  buildCartContainsNonIncentiveProductsSelector(selectCartHasVouchers);

// PreDashboardOffers: hasVouchers is always false here, so this is
// equivalent to the plain cartContainsNonIncentiveProducts check it ran.
export const selectCartContainsNonIncentiveProductsIgnoringVouchers =
  buildCartContainsNonIncentiveProductsSelector(noVouchersSelector);

// ---------------------------------------------------------------------------
// AvailableBonuses (cart's "Available Bonuses" section) — bonuses the parent
// declined but still qualifies for, across every OPT-IN order in the cart
// (ENG-2043 R4). Each order is judged against its own stashed catalog, never
// against the active shoot's, so a bonus declined at school A stays listed
// while school B is open. The active catalog only stands in for the current
// order (the R1 fallback for orders persisted before the stash existed).
// ---------------------------------------------------------------------------

export const selectAvailableOptionalBonuses = createSelector(
  (state: any) => state.v3Order?.orders,
  selectRestrictionAwareIncentiveProducts,
  selectCurrentOrderKeyForFallback,
  selectOptionalIncentivesEnabled,
  selectParentCountry,
  (state: any) => state.v3Order?.optionalIncentiveProductIdsToIgnoreByOrder,
  selectIgnoredCartLevelIncentiveSkus,
  selectCartVoucherTotalCents,
  selectCartHasVouchers,
  (
    orders: any,
    activeIncentiveProducts: any,
    currentOrderKey: any,
    activeOptionalIncentivesEnabled: any,
    parentCountry: any,
    ignoresByOrder: any,
    ignoredCartLevelSkus: any,
    voucherTotalCents: any,
    hasVouchers: any,
  ): any[] =>
    cartUtils.availableOptionalIncentives({
      orders,
      activeIncentiveProducts,
      currentOrderKey,
      activeOptionalIncentivesEnabled: activeOptionalIncentivesEnabled === true,
      parentCountry,
      ignoresByOrder: ignoresByOrder || {},
      ignoredCartLevelSkus,
      voucherTotalCents,
      hasVouchers,
    }),
);

// Cart-level bonuses on the ignore list that can't be matched back to an
// owning order (see cartUtils.ownerOrderForCartIncentive) — reported to
// Sentry rather than silently dropped, since AvailableBonuses has nowhere to
// route a re-add for them.
export const selectUnresolvedOptionalCartLevelIncentives = createSelector(
  (state: any) => state.v3Order?.orders,
  selectCartLevelIncentiveListingUnion,
  selectIgnoredCartLevelIncentiveSkus,
  selectCartVoucherTotalCents,
  (
    orders: any,
    incentiveProducts: any,
    optionalCartLevelIncentiveSkus: any,
    voucherTotalCents: any,
  ): any[] =>
    cartUtils.unresolvedOptionalCartLevelIncentives({
      orders,
      incentiveProducts,
      optionalCartLevelIncentiveSkus,
      voucherTotalCents,
    }),
);

// ---------------------------------------------------------------------------
// IncentivesConfig — the picker that presents the next eligible bonus. It has
// one extra wrinkle the offer gates don't: a bonus can be re-opened from the
// cart's "Available Bonuses" list via v3Order.optionalIncentiveToConfigure,
// which points at a *specific* order that need not be the parent's current
// one. Every selector below resolves against that order when present, falling
// back to the current order otherwise.
// ---------------------------------------------------------------------------

export const selectReAddedIncentiveProductId = (state: any): any =>
  state.v3Order?.optionalIncentiveToConfigure?.productId ?? null;

// Cart-level re-adds are identified by SKU: the row the target order's own
// catalog offers may be a different price list's row for the same benefit.
export const selectReAddedIncentiveSku = (state: any): any =>
  state.v3Order?.optionalIncentiveToConfigure?.sku ?? null;

// The re-add target's own cart items, when optionalIncentiveToConfigure names
// an order — undefined otherwise (including when that order/its cart items no
// longer exist), which is what tells the selectors below to fall back to the
// current order.
const selectOptionalIncentiveOrderCartItems = createSelector(
  selectOptionalIncentiveToConfigure,
  (state: any) => state.v3Order?.orders,
  (optionalIncentiveToConfigure, orders): any[] | undefined =>
    optionalIncentiveToConfigure?.orderKey
      ? orders?.[optionalIncentiveToConfigure.orderKey]?.cartItems
      : undefined,
);

export const selectOrderKeyForIncentiveConfig = createSelector(
  selectOptionalIncentiveToConfigure,
  selectOptionalIncentiveOrderCartItems,
  selectCurrentOrderKey,
  (
    optionalIncentiveToConfigure,
    optionalIncentiveOrderCartItems,
    currentOrderKey,
  ): string | null =>
    optionalIncentiveOrderCartItems
      ? optionalIncentiveToConfigure.orderKey
      : currentOrderKey,
);

export const selectCartItemsForOrderIncentives = createSelector(
  selectOptionalIncentiveOrderCartItems,
  selectCurrentOrderCartItems,
  (optionalIncentiveOrderCartItems, currentOrderCartItems): any[] =>
    optionalIncentiveOrderCartItems || currentOrderCartItems,
);

// The catalog the picker must resolve its target against: the TARGET order's
// own rows (ENG-2043 R2), not the active shoot's — a re-add from the cart can
// name an order from a shoot that is no longer loaded.
export const selectIncentiveConfigCatalog = createSelector(
  (state: any) => state.v3Order?.orders,
  selectOrderKeyForIncentiveConfig,
  selectCurrentOrderKeyForFallback,
  selectRestrictionAwareIncentiveProducts,
  selectParentCountry,
  (
    orders,
    orderKey,
    currentOrderKey,
    activeIncentiveProducts,
    parentCountry,
  ): any[] =>
    cartUtils.restrictionFilteredCatalog(
      cartUtils.incentiveCatalogForOrder(
        orderKey ? orders?.[orderKey] : undefined,
        activeIncentiveProducts,
        { isCurrentOrder: Boolean(orderKey) && orderKey === currentOrderKey },
      ),
      parentCountry,
    ),
);

// A bonus being re-added from the cart is still on the ignore lists until
// V3_ADD_TO_CART clears it (see postConfigRoutingMiddleware), so it is carved
// out of both ignore lists — by id under its order, by SKU on the cart list —
// to keep the re-add flow working.
export const selectIgnoredOrderIncentiveIdsForIncentiveConfig = createSelector(
  selectOrderOptInMap,
  (state: any) => state.v3Order?.optionalIncentiveProductIdsToIgnoreByOrder,
  selectOrderKeyForIncentiveConfig,
  selectReAddedIncentiveProductId,
  (optInByOrderKey, ignoresByOrder, orderKey, reAddedProductId): any[] => {
    const ids =
      orderKey && optInByOrderKey[orderKey]
        ? ignoresByOrder?.[orderKey] || []
        : [];
    return ids.filter((productId: any) => productId !== reAddedProductId);
  },
);

export const selectIgnoredCartLevelIncentiveSkusForIncentiveConfig =
  createSelector(
    selectIgnoredCartLevelIncentiveSkus,
    selectReAddedIncentiveProductId,
    selectReAddedIncentiveSku,
    (ignoredSkus, reAddedProductId, reAddedSku): any[] =>
      ignoredSkus.filter(
        (sku: any) => sku !== reAddedSku && sku !== reAddedProductId,
      ),
  );

export const selectEligibleOrderIncentivesForIncentiveConfig = createSelector(
  selectCartItemsForOrderIncentives,
  selectIncentiveConfigCatalog,
  selectCartVoucherTotalCents,
  selectCartHasVouchers,
  selectIgnoredOrderIncentiveIdsForIncentiveConfig,
  (
    cartItems,
    incentiveProducts,
    voucherTotalCents,
    hasVouchers,
    ignoredIds,
  ): any[] =>
    cartUtils.eligibleIncentivesForOrder(
      cartItems,
      incentiveProducts,
      voucherTotalCents,
      hasVouchers,
      ignoredIds,
    ),
);

// Cart-level candidates come from the union (ENG-2965): entries stashed from
// a non-active catalog are free-shipping benefits with nothing to configure,
// so RegularProductConfig's panel stays closed and Add to Cart is
// immediately reachable.
export const selectEligibleCartIncentivesForIncentiveConfig = createSelector(
  (state: any) => state.v3Order?.orders,
  selectCartLevelIncentiveProducts,
  selectCartVoucherTotalCents,
  selectIgnoredCartLevelIncentiveSkusForIncentiveConfig,
  (orders, incentiveProducts, voucherTotalCents, ignoredSkus): any[] =>
    cartUtils.eligibleIncentivesForCart(
      orders,
      incentiveProducts,
      voucherTotalCents,
      ignoredSkus,
    ),
);

// The specific product a cart re-add points at, once it turns up in either
// eligible list — or null if it no longer qualifies (see
// selectUnresolvedOptionalIncentiveReAdd).
export const selectSelectedOptionalIncentive = createSelector(
  selectOptionalIncentiveToConfigure,
  selectEligibleOrderIncentivesForIncentiveConfig,
  selectEligibleCartIncentivesForIncentiveConfig,
  (
    optionalIncentiveToConfigure,
    eligibleOrderIncentives,
    eligibleCartIncentives,
  ): any =>
    optionalIncentiveToConfigure
      ? [...eligibleOrderIncentives, ...eligibleCartIncentives].find(
          (product) =>
            product.id === optionalIncentiveToConfigure.productId ||
            // A cart-level benefit is the same benefit on every list, so the
            // row the target order offers may not be the row the decline was
            // recorded against (ENG-2043 R3).
            (product.incentive_applies_to === 'cart_level' &&
              optionalIncentiveToConfigure.sku != null &&
              product.sku === optionalIncentiveToConfigure.sku),
        ) || null
      : null,
);

// A cart re-add whose target no longer qualifies (removed from the cart,
// ignore lists changed, etc.) — the picker can't configure it, so
// IncentivesConfig bounces back to the cart instead of rendering blank.
export const selectUnresolvedOptionalIncentiveReAdd = createSelector(
  selectOptionalIncentiveToConfigure,
  selectSelectedOptionalIncentive,
  (optionalIncentiveToConfigure, selectedOptionalIncentive): boolean =>
    Boolean(optionalIncentiveToConfigure && !selectedOptionalIncentive),
);

// The candidate list to present next: the re-add target alone when there is
// one, otherwise every eligible order-level bonus, falling back to cart-level
// bonuses only when no order-level bonus qualifies.
export const selectIncentivesToConfig = createSelector(
  selectSelectedOptionalIncentive,
  selectEligibleOrderIncentivesForIncentiveConfig,
  selectEligibleCartIncentivesForIncentiveConfig,
  (
    selectedOptionalIncentive,
    eligibleOrderIncentives,
    eligibleCartIncentives,
  ): any[] => {
    if (selectedOptionalIncentive) return [selectedOptionalIncentive];
    return eligibleOrderIncentives.length > 0
      ? eligibleOrderIncentives
      : eligibleCartIncentives;
  },
);

export const selectIncentiveProductToConfigure = createSelector(
  selectIncentivesToConfig,
  (incentivesToConfig): any =>
    incentivesToConfig.length > 0 ? incentivesToConfig[0] : null,
);

// The order a cart-level bonus belongs to (ENG-2043 R3). Order-level bonuses
// belong to the order being configured, so this is null for them.
export const selectOwnerOrderKeyForIncentiveProduct = createSelector(
  (state: any) => state.v3Order?.orders,
  selectIncentiveProductToConfigure,
  selectOrderKeyForIncentiveConfig,
  (orders, product, orderKeyForConfig): string | null => {
    if (!product) return null;
    if (product.incentive_applies_to !== 'cart_level') return orderKeyForConfig;
    const owner = cartUtils.ownerOrderForCartIncentive(orders, product);
    return owner ? owner.orderKey : null;
  },
);

// Whether the bonus currently on screen may be declined: the flag of the
// order that earned it — the target order for an order-level bonus, the owner
// order (plus the shared-SKU rule) for a cart-level one. IncentivesConfig's
// "No thanks" affordance and its "you can remove this later" promise are both
// made against this, so opening another gallery cannot change either.
export const selectOptionalIncentivesEnabledForIncentiveConfig = createSelector(
  (state: any) => state.v3Order?.orders,
  selectIncentiveProductToConfigure,
  selectOwnerOrderKeyForIncentiveProduct,
  selectOrderOptInMap,
  selectCurrentOrderKey,
  (
    orders,
    product,
    ownerOrderKey,
    optInByOrderKey,
    currentOrderKey,
  ): boolean => {
    if (!product) return false;
    if (!ownerOrderKey || !optInByOrderKey[ownerOrderKey]) return false;
    if (product.incentive_applies_to !== 'cart_level') return true;

    return cartUtils.cartLevelSkuIsDeclinable(
      orders,
      product.sku != null ? product.sku : product.id,
      optInByOrderKey,
      currentOrderKey ? optInByOrderKey[currentOrderKey] : false,
    );
  },
);

// The retouching edit route names an exact destination, including a non-active
// gallery. Apply the same optional-bonus ignore and re-add rules as the normal
// picker while resolving the catalog from that destination's configuration.
export const selectIncentiveConfiguration = createSelector(
  (state: any, orderKey: string | null) =>
    selectConfigurationContext(state, orderKey),
  (state: any) => state.v3Order?.orders,
  (state: any) => state.cartVoucherItems,
  (state: any) => state.parent,
  selectOrderOptInMap,
  (state: any) => state.v3Order?.optionalIncentiveProductIdsToIgnoreByOrder,
  selectIgnoredCartLevelIncentiveSkus,
  selectOptionalIncentiveToConfigure,
  (
    context,
    orders,
    vouchers,
    parent,
    optInByOrderKey,
    ignoresByOrder,
    ignoredSkus,
    marker,
  ) => {
    if (!context.available) return { available: false, product: null };
    const catalog = availableIncentiveProducts(
      context.shoot?.incentiveProducts,
      isShippingRestrictedForParent(context.shoot?.shoot, parent),
    );
    const reAdd = marker?.orderKey === context.orderKey ? marker : null;
    const ignoredIds = optInByOrderKey[context.orderKey]
      ? (ignoresByOrder?.[context.orderKey] || []).filter(
          (id: any) => id !== reAdd?.productId,
        )
      : [];
    const total = vouchers?.voucherSubtotalCents || 0;
    const eligibleOrderIncentives = cartUtils.eligibleIncentivesForOrder(
      context.order?.cartItems || [],
      catalog,
      total,
      vouchers?.items?.length > 0,
      ignoredIds,
    );
    const eligibleCartIncentives = cartUtils.eligibleIncentivesForCart(
      orders,
      cartUtils.unionCartLevelIncentiveProducts(
        orders,
        catalog,
        parent?.address?.country || 'US',
      ),
      total,
      ignoredSkus.filter(
        (sku: any) => sku !== reAdd?.sku && sku !== reAdd?.productId,
      ),
    );
    const candidates = eligibleOrderIncentives.length
      ? eligibleOrderIncentives
      : eligibleCartIncentives;
    const product = reAdd
      ? [...eligibleOrderIncentives, ...eligibleCartIncentives].find(
          (candidate: any) =>
            candidate.id === reAdd.productId ||
            (candidate.incentive_applies_to === 'cart_level' &&
              reAdd.sku != null &&
              candidate.sku === reAdd.sku),
        ) ?? null
      : candidates[0] ?? null;
    const ownerKey =
      product?.incentive_applies_to === 'cart_level'
        ? cartUtils.ownerOrderForCartIncentive(orders, product)?.orderKey ??
          context.orderKey
        : context.orderKey;
    const optional = Boolean(
      product &&
        optInByOrderKey[ownerKey] &&
        (product.incentive_applies_to !== 'cart_level' ||
          cartUtils.cartLevelSkuIsDeclinable(
            orders,
            product.sku ?? product.id,
            optInByOrderKey,
            optInByOrderKey[context.orderKey],
          )),
    );
    return {
      available: true,
      product,
      optional,
      orderKey: ownerKey,
      eligibleOrderIncentives,
      eligibleCartIncentives,
    };
  },
);
